import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  tradersApi,
  adminTradersApi,
  ApiError,
  type TraderMessage,
  type TraderPosition,
  type TraderProfile,
} from '~/lib/api';
import { useAuth } from '~/contexts/AuthContext';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { InlineError } from '~/components/ErrorPage';
import { PositionStatusLabel } from '~/components/PositionStatusLabel';
import { StatusFilterPill } from '~/components/StatusFilterPill';

/** Parse DB UTC timestamp or ISO 8601 string. */
function parseUtc(ts: string): Date {
  if (!ts) return new Date(NaN);
  if (ts.includes('T')) return new Date(ts);
  return new Date(ts.replace(' ', 'T') + 'Z');
}

function formatTime(ts: string): string {
  return parseUtc(ts).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelative(ts: string): string {
  return formatDistanceToNow(parseUtc(ts), { addSuffix: true });
}

function extractImageUrls(rawPayload: string | null | undefined): string[] {
  if (!rawPayload) return [];
  try {
    const payload = JSON.parse(rawPayload) as {
      attachments?: Array<{ url?: string; type?: string; mimeType?: string | null }>;
    };
    const urls: string[] = [];
    for (const a of payload.attachments ?? []) {
      if (!a.url) continue;
      const mime = a.mimeType || a.type || '';
      if (mime.startsWith('image/')) {
        urls.push(a.url);
        continue;
      }
      if (/\.(png|jpe?g|gif|webp)(\?|$)/i.test(a.url)) {
        urls.push(a.url);
      }
    }
    return urls;
  } catch {
    return [];
  }
}

// ── notification mode ──────────────────────────────────────────────────────
// Three user-visible modes. "Messages only" was dropped — if you want chat
// pings you almost always want plays too, so the prefs collapse to:
//   Silent              → (0, 0)
//   Plays               → (1, 0)
//   Plays & Messages    → (1, 1)
// Legacy (0, 1) rows from before this change are surfaced as "Plays & Messages".
type NotifyMode = 'silent' | 'plays' | 'plays_and_messages';

function modeFromProfile(p: TraderProfile | undefined): NotifyMode {
  if (!p) return 'silent';
  if (p.notify_messages) return 'plays_and_messages'; // implies plays as well
  if (p.notify_plays) return 'plays';
  return 'silent';
}

function flagsFromMode(mode: NotifyMode) {
  return {
    notify_plays: mode === 'plays' || mode === 'plays_and_messages',
    notify_messages: mode === 'plays_and_messages',
  };
}

// 'silent' isn't a pill — it's the default state when none is selected.
// Clicking the active pill again toggles back to silent.
const NOTIFY_OPTIONS: Array<{ mode: 'plays' | 'plays_and_messages'; label: string }> = [
  { mode: 'plays', label: 'Plays' },
  { mode: 'plays_and_messages', label: 'Plays & Messages' },
];

function TraderDetailContent() {
  const { traderId } = useParams<{ traderId: string }>();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const { data: profile } = useQuery({
    queryKey: ['trader-profile', traderId],
    queryFn: () => tradersApi.get(traderId!),
    enabled: !!traderId,
  });

  // Optimistic hide toggle (admin-only). When the operator hides a trader
  // their signals/messages stop reaching non-admin clients but stay visible
  // to admins for moderation.
  const hideMutation = useMutation({
    mutationFn: (hidden: boolean) => adminTradersApi.setHidden(traderId!, hidden),
    onMutate: (hidden) => {
      const prev = queryClient.getQueryData<TraderProfile>(['trader-profile', traderId]);
      if (prev) {
        queryClient.setQueryData<TraderProfile>(['trader-profile', traderId], { ...prev, hidden });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['trader-profile', traderId], ctx.prev);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trader-profile', traderId] });
    },
  });

  const { data: messagesData, isLoading, error } = useQuery({
    queryKey: ['trader-messages', traderId],
    queryFn: () => tradersApi.messages(traderId!, 100),
    enabled: !!traderId,
  });

  const { data: positionsData } = useQuery({
    queryKey: ['trader-positions', traderId],
    queryFn: () => tradersApi.positions(traderId!, 'all'),
    enabled: !!traderId,
  });

  // Optimistic notification updates: when the user picks a mode the local
  // profile is updated immediately and the server call fires; on success we
  // invalidate to reconcile.
  const notifyMutation = useMutation({
    mutationFn: (mode: NotifyMode) =>
      tradersApi.updateNotifications(traderId!, flagsFromMode(mode)),
    onMutate: (mode) => {
      const prev = queryClient.getQueryData<TraderProfile>(['trader-profile', traderId]);
      if (prev) {
        queryClient.setQueryData<TraderProfile>(['trader-profile', traderId], {
          ...prev,
          followed: true,
          ...flagsFromMode(mode),
        });
      }
      return { prev };
    },
    onError: (_err, _mode, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['trader-profile', traderId], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['trader-profile', traderId] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      // Also invalidate the traders list cache so the row pills there
      // reflect the new mode immediately, not after the 30s staleTime.
      queryClient.invalidateQueries({ queryKey: ['push', 'traders'] });
    },
  });

  // Two tabs: Plays (with a status sub-filter) and Messages.
  // Hooks must be declared BEFORE any conditional return below.
  const [activeTab, setActiveTab] = useState<'plays' | 'messages'>('plays');
  // null = no filter (show all plays). Clicking a pill again clears it.
  const [playsFilter, setPlaysFilter] = useState<'active' | 'watching' | 'closed' | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error) {
    return <InlineError error={error instanceof ApiError ? error : (error as Error)} />;
  }

  const messages = messagesData?.messages ?? [];
  // Display name: prefer the profile's current_nick, fall back to the first
  // message's projected current_nick (so the header still shows something
  // sensible before profile resolves).
  const displayName = profile?.current_nick ?? messages?.[0]?.author_username ?? '';
  // Backend groups [watching, open, partial] under `open` and
  // [closed, expired] under `closed`. Split into the same three buckets
  // the plays page filter uses: Active = open|partial, Watching, Closed.
  const allOpen = positionsData?.open ?? [];
  const activePositions = allOpen.filter(
    (p) => p.position_status === 'open' || p.position_status === 'partial',
  );
  const watchingPositions = allOpen.filter((p) => p.position_status === 'watching');
  const closedPositions = positionsData?.closed ?? [];
  const currentMode = modeFromProfile(profile);

  // Group messages by day for readability (only used when the messages tab
  // is selected, but cheap enough to compute once)
  const byDay = new Map<string, TraderMessage[]>();
  for (const m of messages) {
    const day = parseUtc(m.timestamp).toISOString().slice(0, 10);
    let arr = byDay.get(day);
    if (!arr) {
      arr = [];
      byDay.set(day, arr);
    }
    arr.push(m);
  }
  const dayKeys = Array.from(byDay.keys());

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="card p-4 sm:p-5 space-y-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-mono text-text-primary">
            @{displayName}
          </h1>
          {messages.length > 0 && (
            <p className="text-sm text-text-muted mt-1">
              last seen {formatRelative(messages[0].timestamp)}
            </p>
          )}
        </div>

        {/* Admin-only hide toggle. Hidden traders disappear from non-admin
            reads + realtime broadcasts but admins keep visibility. */}
        {isAdmin && profile && (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-mono uppercase tracking-widest text-text-muted">
              Visibility
            </p>
            <button
              type="button"
              onClick={() => hideMutation.mutate(!profile.hidden)}
              disabled={hideMutation.isPending}
              className={clsx(
                'px-3 py-1.5 rounded text-xs font-mono font-medium transition-colors disabled:opacity-50',
                profile.hidden
                  ? 'bg-loss/20 text-loss hover:bg-loss/30'
                  : 'bg-surface-2 text-text-secondary hover:bg-surface-3 hover:text-text-primary',
              )}
              title={
                profile.hidden
                  ? 'Trader is hidden from non-admin users. Click to un-hide.'
                  : 'Hide this trader from non-admin users (signals still ingested for admin review).'
              }
            >
              {profile.hidden ? 'Hidden — click to show' : 'Hide from non-admins'}
            </button>
          </div>
        )}

        {/* Push notification mode. Silent = following silently (no push).
            Plays / Messages / All opt in to the corresponding push events. */}
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs font-mono uppercase tracking-widest text-text-muted">
            Get notified
          </p>
          <div className="flex flex-wrap gap-1">
            {NOTIFY_OPTIONS.map(({ mode, label }) => {
              const active = currentMode === mode;
              // Click active again → back to silent (no pill highlighted).
              const next: NotifyMode = active ? 'silent' : mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => notifyMutation.mutate(next)}
                  disabled={notifyMutation.isPending || !profile}
                  className={clsx(
                    'px-3 py-1.5 rounded text-xs font-mono font-medium transition-colors disabled:opacity-50',
                    active
                      ? 'bg-accent text-surface-0'
                      : 'bg-surface-2 text-text-secondary hover:bg-surface-3 hover:text-text-primary',
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top-level tabs: Plays / Messages */}
      <div className="flex flex-wrap gap-1 border-b border-border">
        <TabButton active={activeTab === 'plays'} onClick={() => setActiveTab('plays')}>
          Plays <span className="text-text-muted">({activePositions.length + watchingPositions.length + closedPositions.length})</span>
        </TabButton>
        <TabButton active={activeTab === 'messages'} onClick={() => setActiveTab('messages')}>
          Messages <span className="text-text-muted">({messages.length})</span>
        </TabButton>
      </div>

      {activeTab === 'plays' && (() => {
        const filtered =
          playsFilter === 'active' ? activePositions :
          playsFilter === 'watching' ? watchingPositions :
          playsFilter === 'closed' ? closedPositions :
          [...activePositions, ...watchingPositions, ...closedPositions];
        // Click the active pill again to clear the filter (back to all).
        const toggle = (f: 'active' | 'watching' | 'closed') =>
          setPlaysFilter((prev) => (prev === f ? null : f));
        return (
          <div className="space-y-3">
            {/* Status filter — pills coloured to match the plays-table status text.
                No 'All' pill: default state (no selection) already means all. */}
            <div className="flex flex-wrap gap-1">
              <StatusFilterPill
                active={playsFilter === 'active'}
                onClick={() => toggle('active')}
                colorClass="text-accent"
                activeBgClass="bg-accent/15"
              >
                Active ({activePositions.length})
              </StatusFilterPill>
              <StatusFilterPill
                active={playsFilter === 'watching'}
                onClick={() => toggle('watching')}
                colorClass="text-blue-400"
                activeBgClass="bg-blue-400/15"
              >
                Watch ({watchingPositions.length})
              </StatusFilterPill>
              <StatusFilterPill
                active={playsFilter === 'closed'}
                onClick={() => toggle('closed')}
                colorClass="text-loss"
                activeBgClass="bg-loss/15"
              >
                Closed ({closedPositions.length})
              </StatusFilterPill>
            </div>
            <PositionsList positions={filtered} emptyText={`No ${playsFilter ? playsFilter + ' ' : ''}positions for @${displayName}.`} />
          </div>
        );
      })()}
      {activeTab === 'messages' && (
        messages.length === 0 ? (
          <div className="card p-8 text-center">
            <p className="text-text-secondary">No messages captured for @{displayName} yet.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {dayKeys.map((day) => (
              <div key={day} className="space-y-3">
                <h3 className="text-xs font-mono uppercase tracking-widest text-text-muted">
                  {day}
                </h3>
                <div className="space-y-2">
                  {byDay.get(day)!.map((m) => (
                    <MessageCard key={m.id} message={m} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2',
        active
          ? 'border-accent text-text-primary'
          : 'border-transparent text-text-secondary hover:text-text-primary',
      )}
    >
      {children}
    </button>
  );
}


function PositionsList({
  positions,
  emptyText,
}: {
  positions: TraderPosition[];
  emptyText: string;
}) {
  if (positions.length === 0) {
    return (
      <div className="card p-8 text-center">
        <p className="text-text-secondary">{emptyText}</p>
      </div>
    );
  }
  return (
    <div className="card">
      <table className="w-full">
        <thead className="glass-table-header">
          <tr>
            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium">
              Symbol
            </th>
            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium whitespace-nowrap">
              Entry
            </th>
            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium whitespace-nowrap">
              Exit
            </th>
            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium">
              Status
            </th>
            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium hidden sm:table-cell">
              Expiry
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {positions.map((p) => (
            <PositionRow key={p.position_id} pos={p} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PositionRow({ pos }: { pos: TraderPosition }) {
  return (
    <tr className="hover:bg-surface-2/50 transition-colors">
      <td className="px-2 sm:px-4 py-2 sm:py-3">
        <Link
          to={`/plays/${pos.position_id}`}
          className="font-medium font-mono text-text-primary text-xs sm:text-base whitespace-nowrap hover:text-accent"
        >
          {pos.ticker} {pos.strike}{pos.option_type}
        </Link>
        {pos.expiry && (
          <div className="text-xs text-text-muted sm:hidden">{pos.expiry}</div>
        )}
      </td>
      <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-mono tabular-nums whitespace-nowrap">
        {pos.entry_price ? (
          <span className="text-text-secondary">${pos.entry_price.toFixed(2)}</span>
        ) : (
          <span className="text-text-muted">-</span>
        )}
      </td>
      <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-mono tabular-nums whitespace-nowrap">
        {pos.exit_price ? (
          <span className="text-text-secondary">${pos.exit_price.toFixed(2)}</span>
        ) : (
          <span className="text-text-muted">-</span>
        )}
      </td>
      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
        <PositionStatusLabel status={pos.position_status} remainingPct={pos.remaining_pct} pnlPercent={pos.realized_pnl} />
      </td>
      <td className="px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell text-xs text-text-muted whitespace-nowrap">
        {pos.expiry || (pos.expiry_type === '0dte' ? '0DTE' : '')}
      </td>
    </tr>
  );
}

function MessageCard({ message }: { message: TraderMessage }) {
  const imageUrls = extractImageUrls(message.raw_payload);
  const isAlert = message.channel_type === 'alerts';
  const hasReply = !!message.reply_to_author;

  return (
    <div
      className={clsx(
        'card p-3 sm:p-4 space-y-2',
        isAlert && 'border-warning/30'
      )}
    >
      {hasReply && (
        <div className="text-xs text-text-muted border-l-2 border-border pl-2 truncate">
          <span className="text-text-secondary">↩ Replying to @{message.reply_to_author}</span>
          {message.reply_to_content && (
            <span className="ml-1 text-text-muted">· "{message.reply_to_content}"</span>
          )}
        </div>
      )}

      <div className="flex items-baseline gap-2 flex-wrap text-xs text-text-muted">
        <span className="font-mono">{formatTime(message.timestamp)}</span>
        {message.channel_name && (
          <span className="text-text-muted">· #{message.channel_name}</span>
        )}
        {isAlert && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider bg-warning/15 text-warning">
            alert
          </span>
        )}
        {message.is_edited ? (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-surface-3 text-text-muted">
            edited
          </span>
        ) : null}
      </div>

      {message.content && (
        <p className="text-sm text-text-primary whitespace-pre-wrap break-words">
          {message.content}
        </p>
      )}

      {imageUrls.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {imageUrls.map((url, i) => (
            <a
              key={i}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block max-w-sm overflow-hidden rounded border border-border hover:border-accent transition-colors"
            >
              <img
                src={url}
                alt={`attachment ${i + 1}`}
                loading="lazy"
                className="max-h-80 w-auto object-contain"
              />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TraderDetailPage() {
  return (
    <ErrorBoundary>
      <TraderDetailContent />
    </ErrorBoundary>
  );
}
